/**
 * useFeedProfile Hook
 * Upravljanje feed profilima (dobavljači)
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { supabase } from '../../../lib/supabase';
import { adminApi } from '../../../lib/adminApi';
import { FeedProfile, CustomField, ImportMode, SoapConfig, RowFilter } from '../types';
import { DEFAULT_UPDATE_FIELDS } from '../utils/fieldConfig';
import { FieldTransformOptions } from '../../../lib/xmlParser';

const DEFAULT_SOAP_CONFIG: SoapConfig = {
  endpoint_url: '',
  soap_action: '',
  method: '',
  namespace: '',
  username: '',
  password: '',
};

export interface UseFeedProfileReturn {
  // State
  profiles: FeedProfile[];
  selectedProfile: FeedProfile | null;
  loading: boolean;

  // Profile data
  feedName: string;
  setFeedName: (name: string) => void;
  feedUrl: string;
  setFeedUrl: (url: string) => void;
  feedType: 'xml' | 'soap' | 'excel';
  setFeedType: (type: 'xml' | 'soap' | 'excel') => void;
  authType: 'none' | 'url_params' | 'basic';
  setAuthType: (type: 'none' | 'url_params' | 'basic') => void;
  authUsername: string;
  setAuthUsername: (username: string) => void;
  authPassword: string;
  setAuthPassword: (password: string) => void;
  soapConfig: SoapConfig;
  setSoapConfig: (config: SoapConfig) => void;

  // Mappings
  mappings: Record<string, string>;
  setMappings: React.Dispatch<React.SetStateAction<Record<string, string>>>;

  // Field options
  fieldOptions: Record<string, FieldTransformOptions>;
  setFieldOptions: React.Dispatch<React.SetStateAction<Record<string, FieldTransformOptions>>>;

  // Custom fields
  customFields: CustomField[];
  setCustomFields: React.Dispatch<React.SetStateAction<CustomField[]>>;

  // Update fields
  updateFields: Record<string, boolean>;
  setUpdateFields: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

  // Import mode
  importMode: ImportMode;
  setImportMode: React.Dispatch<React.SetStateAction<ImportMode>>;

  // Skip duplicate check
  skipDuplicateCheck: boolean;
  setSkipDuplicateCheck: React.Dispatch<React.SetStateAction<boolean>>;

  // Delivery days
  deliveryDays: number;
  setDeliveryDays: React.Dispatch<React.SetStateAction<number>>;

  // Row filters
  rowFilters: RowFilter[];
  setRowFilters: React.Dispatch<React.SetStateAction<RowFilter[]>>;

  // Extra headers
  extraHeaders: Record<string, string>;
  setExtraHeaders: React.Dispatch<React.SetStateAction<Record<string, string>>>;

  // Actions
  loadProfiles: () => Promise<void>;
  selectProfile: (profile: FeedProfile) => void;
  saveProfile: () => Promise<boolean>;
  clearProfile: () => void;
}

export function useFeedProfile(): UseFeedProfileReturn {
  // Profiles list
  const [profiles, setProfiles] = useState<FeedProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<FeedProfile | null>(null);
  const [loading, setLoading] = useState(false);

  // Profile config
  const [feedName, setFeedName] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [feedType, setFeedType] = useState<'xml' | 'soap' | 'excel'>('xml');
  const [authType, setAuthType] = useState<'none' | 'url_params' | 'basic'>('none');
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [soapConfig, setSoapConfig] = useState<SoapConfig>(DEFAULT_SOAP_CONFIG);

  // Mappings & options
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [fieldOptions, setFieldOptions] = useState<Record<string, FieldTransformOptions>>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [updateFields, setUpdateFields] = useState<Record<string, boolean>>(DEFAULT_UPDATE_FIELDS);
  const [importMode, setImportMode] = useState<ImportMode>('create_and_update');
  const [skipDuplicateCheck, setSkipDuplicateCheck] = useState<boolean>(false);
  const [deliveryDays, setDeliveryDays] = useState<number>(0);
  const [rowFilters, setRowFilters] = useState<RowFilter[]>([]);
  const [extraHeaders, setExtraHeaders] = useState<Record<string, string>>({});

  /**
   * Učitaj sve profile iz baze
   */
  const loadProfiles = useCallback(async () => {
    try {
      const data = await adminApi.suppliers.list();

      const feedProfiles: FeedProfile[] = (data || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        feed_url: s.feed_url || '',
        feed_type: s.feed_type === 'soap' ? 'soap' : (s.feed_type === 'excel' ? 'excel' : 'xml'),
        auth_type: s.auth_type || (s.feed_username ? 'url_params' : 'none'),
        auth_config: s.feed_username
          ? { username: s.feed_username, password: s.feed_password }
          : undefined,
        soap_config: s.soap_config || undefined,
        field_mappings: s.field_mappings || {},
        field_options: s.field_options || {},
        custom_fields: s.custom_fields || [],
        update_fields: s.update_fields || undefined,
        row_filters: s.row_filters || [],
        extra_headers: s.extra_headers || {},
        import_mode: s.import_mode || 'create_and_update',
        skip_duplicate_check: s.skip_duplicate_check || false,
        delivery_days: s.delivery_days || 0,
        enabled: s.enabled,
        last_import: s.last_import_at,
      }));

      setProfiles(feedProfiles);
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  }, []);

  /**
   * Odaberi profil i popuni formu
   */
  const selectProfile = useCallback((profile: FeedProfile) => {
    setSelectedProfile(profile);
    setFeedName(profile.name);
    setFeedUrl(profile.feed_url);
    setFeedType(profile.feed_type || 'xml');
    setAuthType(profile.auth_type);
    setAuthUsername(profile.auth_config?.username || '');
    setAuthPassword(profile.auth_config?.password || '');
    setSoapConfig(profile.soap_config || DEFAULT_SOAP_CONFIG);
    setMappings(profile.field_mappings || {});
    setFieldOptions(profile.field_options || {});
    setCustomFields(profile.custom_fields || []);
    if (profile.update_fields) {
      setUpdateFields(profile.update_fields);
    }
    setImportMode(profile.import_mode || 'create_and_update');
    setSkipDuplicateCheck(profile.skip_duplicate_check || false);
    setDeliveryDays(profile.delivery_days || 0);
    setRowFilters(profile.row_filters || []);
    setExtraHeaders(profile.extra_headers || {});
  }, []);

  /**
   * Spremi profil u bazu
   */
  const saveProfile = useCallback(async (): Promise<boolean> => {
    if (!feedName) {
      toast.error('Unesi naziv profila');
      return false;
    }

    if (!mappings['sku']) {
      toast.error('SKU mapiranje je obavezno');
      return false;
    }

    setLoading(true);

    try {
      const profileData = {
        name: feedName,
        feed_url: feedType === 'soap' ? null : feedUrl,
        feed_type: feedType,
        auth_type: feedType === 'xml' && authType !== 'none' ? authType : null,
        feed_username: feedType === 'xml' && authType !== 'none' ? authUsername : null,
        feed_password: feedType === 'xml' && authType !== 'none' ? authPassword : null,
        soap_config: feedType === 'soap' ? soapConfig : null,
        field_mappings: mappings,
        field_options: fieldOptions,
        custom_fields: customFields,
        update_fields: updateFields,
        row_filters: rowFilters.length > 0 ? rowFilters : null,
        extra_headers: Object.keys(extraHeaders).length > 0 ? extraHeaders : null,
        import_mode: importMode,
        skip_duplicate_check: skipDuplicateCheck,
        delivery_days: deliveryDays,
        enabled: true,
      };

      if (selectedProfile?.id) {
        // Update existing
        await adminApi.suppliers.update(selectedProfile.id, profileData);
        toast.success('Profil ažuriran');
      } else {
        // Create new
        await adminApi.suppliers.create(profileData);
        toast.success('Profil spremljen');
      }

      await loadProfiles();
      return true;
    } catch (error: any) {
      toast.error('Greška pri spremanju: ' + error.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    feedName, feedUrl, feedType, authType, authUsername, authPassword, soapConfig,
    mappings, fieldOptions, customFields, updateFields, rowFilters, extraHeaders, importMode, skipDuplicateCheck, deliveryDays,
    selectedProfile, loadProfiles
  ]);

  /**
   * Očisti formu za novi profil
   */
  const clearProfile = useCallback(() => {
    setSelectedProfile(null);
    setFeedName('');
    setFeedUrl('');
    setFeedType('xml');
    setAuthType('none');
    setAuthUsername('');
    setAuthPassword('');
    setSoapConfig(DEFAULT_SOAP_CONFIG);
    setMappings({});
    setFieldOptions({});
    setCustomFields([]);
    setUpdateFields(DEFAULT_UPDATE_FIELDS);
    setImportMode('create_and_update');
    setSkipDuplicateCheck(false);
    setDeliveryDays(0);
    setRowFilters([]);
    setExtraHeaders({});
  }, []);

  return {
    // State
    profiles,
    selectedProfile,
    loading,

    // Profile data
    feedName,
    setFeedName,
    feedUrl,
    setFeedUrl,
    feedType,
    setFeedType,
    authType,
    setAuthType,
    authUsername,
    setAuthUsername,
    authPassword,
    setAuthPassword,
    soapConfig,
    setSoapConfig,

    // Mappings
    mappings,
    setMappings,

    // Field options
    fieldOptions,
    setFieldOptions,

    // Custom fields
    customFields,
    setCustomFields,

    // Update fields
    updateFields,
    setUpdateFields,

    // Import mode
    importMode,
    setImportMode,

    // Skip duplicate check
    skipDuplicateCheck,
    setSkipDuplicateCheck,

    // Delivery days
    deliveryDays,
    setDeliveryDays,

    // Row filters
    rowFilters,
    setRowFilters,

    // Extra headers
    extraHeaders,
    setExtraHeaders,

    // Actions
    loadProfiles,
    selectProfile,
    saveProfile,
    clearProfile,
  };
}
